import { assign, fromPromise, setup } from 'xstate';
import { createErrorAssigner, handleServiceError } from '~/features/alerts/errors';
import { ServiceError } from '~/features/errors';
import { EmployerUser } from '~/types';
import { getData } from '~/utils/service';

/**
 * Employer Service Machine
 *
 * Responsibilities
 *  - Fetches employerUser data for current user
 */

// Custom error messages for employer service
const employerErrorMap = {
  401: { code: 'UNAUTHORIZED', message: 'Please log in to access employer data' },
  403: { code: 'FORBIDDEN', message: 'You do not have permission to access this employer' },
  404: { code: 'NOT_FOUND', message: 'Employer not found' },
  500: { code: 'SERVER_ERROR', message: 'Server error occurred while fetching employer data' },
};

export type EmployerProviderContext = {
  employerUser: EmployerUser | undefined;
  loading: boolean;
  error: ServiceError | null;
};

export type EmployerProviderEvents = {
  type: 'ON_LOAD';
  payload: { employerUser: EmployerUser | undefined };
};

export type EmployerNameUpdatedEvent = {
  type: 'ON_EMPLOYER_NAME_UPDATED';
  payload: { employerName: string };
};

export const employerService = setup({
  types: {
    context: {} as EmployerProviderContext,
    events: {} as
      | EmployerProviderEvents
      | { type: 'ON_GET_EMPLOYER' }
      | { type: 'ON_REFRESH' }
      | EmployerNameUpdatedEvent
      | { type: string; error?: unknown },
    input: {} as Partial<EmployerProviderContext>,
  },
  actors: {
    fetchEmployerData: fromPromise(async () => {
      return getData<EmployerUser>('employerUser');
    }),
  },
  actions: {
    clearError: assign({ error: null }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    handleServiceError: handleServiceError as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    assignEmployerError: createErrorAssigner(employerErrorMap) as any,
  },
}).createMachine({
  id: 'employerService',
  initial: '$_INITIAL',
  context: () => ({
    employerUser: undefined,
    loading: false,
    error: null,
  }),
  states: {
    $_INITIAL: {
      always: '$_LOADING',
    },
    $_LOADING: {
      entry: [assign({ loading: true }), 'clearError'],
      invoke: {
        src: 'fetchEmployerData',
        input: {},
        onDone: {
          target: '$_SUCCESS',
          actions: [
            assign({
              employerUser: ({ event }) => event.output,
              loading: false,
            }),
          ],
        },
        onError: {
          target: '$_ERROR',
          actions: ['handleServiceError', 'assignEmployerError', assign({ loading: () => false })],
        },
      },
    },
    $_SUCCESS: {
      entry: 'clearError',
      on: {
        // Keeps the cached employerUser in step with a saved Legal Entity Name, so a market
        // actor that re-reads this service after a remount does not see the old name.
        ON_EMPLOYER_NAME_UPDATED: {
          actions: assign({
            employerUser: ({ context, event }) =>
              context.employerUser && {
                ...context.employerUser,
                employer: {
                  ...context.employerUser.employer,
                  employerName: (event as EmployerNameUpdatedEvent).payload.employerName,
                },
              },
          }),
        },
        ON_REFRESH: {
          target: '$_LOADING',
        },
      },
    },
    $_ERROR: {
      on: {
        ON_GET_EMPLOYER: {
          target: '$_LOADING',
        },
      },
    },
  },
});
